import {Color, Tag, TagColor, TagSize} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {
  ADVANCED_ANALYSIS_ACCESS_MESSAGE,
  isAnalysisToolBoxPlotEnabled,
  isExperimentOptimizationEnabled,
  isRegressionsEnabled,
} from 'analysis-tool/AnalysisToolFeatureGates';
import getOutputStepDataForDataFrame from 'analysis-tool/data/getOutputStepDataForDataFrame';
import getLegacyStepFromAnalysis from 'analysis-tool/data/getStepFromAnalysis';
import {AnalysisRightSidebarItem} from 'analysis-tool/right-sidebar/RightSidebarTypes';
import {assert} from 'Asserts';
import {DataFrameColumns} from 'data-frames/DataFrameTypes';
import {
  GetDataFrameColumns,
  GetDataFrameColumnsVariables,
} from 'data-frames/graphql/__generated/GetDataFrameColumns';
import {GET_DATA_FRAME_COLUMNS_QUERY} from 'data-frames/graphql/GetDataFrame';
import {AnalyticsVisualizationBarChartType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import updateVisualization from 'insights/analytics-visualizations/data/updateVisualization';
import {
  VisualizationConfig,
  VisualizationType,
} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import staticURL from 'lib/staticURL';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {ZINDEX_FLOATING_UI} from 'styles/StyleConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/bs/Tooltip';
import * as Colors from 'ui-library/Colors';
import Float from 'ui-library/floating-ui/Float';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import createViewAndOpenChartOrTableView from '../createViewAndOpenChartOrTableView';
import createViewAndOpenExperimentOptimizationView from '../createViewAndOpenExperimentOptimizationView';
import createViewAndOpenRegressionView from '../createViewAndOpenRegressionView';
import {AnalysisMainViewItem, DataReference} from '../main-view/MainViewTypes';
import {ChartOptionType} from './ChartingTypes';
import {getDefaultVisualizationConfigGQL} from './getDefaultVisualizationConfig';

type AnalyticsVisualizationParameters = AnalyticsVisualization['parameters'];

enum ChartTypeCategory {
  TABLE = 'Table',
  BAR = 'Bar',
  OTHER = 'Other charts',
  ML = 'AI & Machine Learning',
}

const VISUALIZATION_CHART_TYPES = [
  ChartOptionType.TABLE,
  ChartOptionType.GROUPED_BAR,
  ChartOptionType.STACKED_BAR,
  ChartOptionType.LINE,
  ChartOptionType.PIE,
  ChartOptionType.SCATTER,
  ChartOptionType.HEATMAP,
  ChartOptionType.BOXPLOT,
  ChartOptionType.EXPERIMENT_OPTIMIZATION,
] as const;

export type VisualizationChartType = (typeof VISUALIZATION_CHART_TYPES)[number];

export const CHART_OPTION_TYPE_TO_VISUALIZATION_TYPE: Record<
  Exclude<VisualizationChartType, ChartOptionType.BOXPLOT | ChartOptionType.EXPERIMENT_OPTIMIZATION>,
  VisualizationType
> = {
  [ChartOptionType.TABLE]: VisualizationType.TABLE,
  [ChartOptionType.GROUPED_BAR]: VisualizationType.BAR_CHART,
  [ChartOptionType.STACKED_BAR]: VisualizationType.BAR_CHART,
  [ChartOptionType.LINE]: VisualizationType.LINE_CHART,
  [ChartOptionType.PIE]: VisualizationType.PIE_CHART,
  [ChartOptionType.SCATTER]: VisualizationType.SCATTER_PLOT,
  [ChartOptionType.HEATMAP]: VisualizationType.HEATMAP,
};

export const CHART_OPTION_TYPE_TO_SVG_PATH: Record<ChartOptionType, string> = {
  [ChartOptionType.TABLE]: '/static/img/insights/table.svg',
  [ChartOptionType.GROUPED_BAR]: '/static/img/insights/grouped_bar_chart_v2.svg',
  [ChartOptionType.STACKED_BAR]: '/static/img/insights/stacked_bar_chart_v2.svg',
  [ChartOptionType.LINE]: '/static/img/insights/line_chart.svg',
  [ChartOptionType.PIE]: '/static/img/insights/pie_chart.svg',
  [ChartOptionType.SCATTER]: '/static/img/insights/scatter_plot.svg',
  [ChartOptionType.HEATMAP]: '/static/img/insights/heatmap.svg',
  [ChartOptionType.REGRESSION]: '/static/img/insights/regression.svg',
  [ChartOptionType.BOXPLOT]: '/static/img/insights/boxplot.svg',
  [ChartOptionType.EXPERIMENT_OPTIMIZATION]: '/static/img/insights/experiment_optimization.svg',
};

/** @package */
export const CHART_OPTION_TYPE_TO_CREATE_VIEW_FN: Record<
  ChartOptionType,
  (params: {
    analysis: PipelineGraphQL;
    inputDataFrameId: string;
    inputDataReference: DataReference;
    onOpenStepInitializationView: (newSelectedItem: AnalysisMainViewItem) => void;
    onChangeSelectedRightSidebarItem: (newSelectedRightSidebarItem: AnalysisRightSidebarItem | null) => void;
    initialVisualizationConfig?: VisualizationConfig;
    chartOptionType: ChartOptionType;
    dataFrameColumns: DataFrameColumns;
  }) => Promise<void> | void
> = {
  [ChartOptionType.TABLE]: createViewAndOpenChartOrTableView,
  [ChartOptionType.GROUPED_BAR]: createViewAndOpenChartOrTableView,
  [ChartOptionType.STACKED_BAR]: createViewAndOpenChartOrTableView,
  [ChartOptionType.LINE]: createViewAndOpenChartOrTableView,
  [ChartOptionType.PIE]: createViewAndOpenChartOrTableView,
  [ChartOptionType.SCATTER]: createViewAndOpenChartOrTableView,
  [ChartOptionType.HEATMAP]: createViewAndOpenChartOrTableView,
  [ChartOptionType.REGRESSION]: createViewAndOpenRegressionView,
  [ChartOptionType.BOXPLOT]: createViewAndOpenChartOrTableView,
  [ChartOptionType.EXPERIMENT_OPTIMIZATION]: createViewAndOpenExperimentOptimizationView,
};

interface ChartOptionWithVisualizationConfig {
  optionType: Exclude<
    VisualizationChartType,
    ChartOptionType.REGRESSION | ChartOptionType.BOXPLOT | ChartOptionType.EXPERIMENT_OPTIMIZATION
  >;
  disabledMessage?: null | string;
  imagePath: string;
  createVisualizationConfig: (dataFrameColumns: DataFrameColumns) => VisualizationConfig;
  isBeta?: true;
  showLabel?: true;
}

interface ChartOptionWithoutVisualizationConfig {
  optionType: ChartOptionType.REGRESSION | ChartOptionType.BOXPLOT | ChartOptionType.EXPERIMENT_OPTIMIZATION;
  disabledMessage?: null | string;
  imagePath: string;
  isBeta?: true;
  showLabel?: true;
}

type ChartOption = ChartOptionWithVisualizationConfig | ChartOptionWithoutVisualizationConfig;

function isChartOptionWithVisualizationConfig(
  chartOption: ChartOption
): chartOption is ChartOptionWithVisualizationConfig {
  return (
    chartOption.optionType !== ChartOptionType.REGRESSION &&
    chartOption.optionType !== ChartOptionType.BOXPLOT &&
    chartOption.optionType !== ChartOptionType.EXPERIMENT_OPTIMIZATION
  );
}

function makeChartTypeOption(chartOptionType: ChartOptionType): ChartOption {
  const baseChartOption = {
    optionType: chartOptionType,
    imagePath: CHART_OPTION_TYPE_TO_SVG_PATH[chartOptionType],
  };

  switch (chartOptionType) {
    case ChartOptionType.REGRESSION:
      return {
        optionType: chartOptionType,
        disabledMessage: isRegressionsEnabled() ? null : ADVANCED_ANALYSIS_ACCESS_MESSAGE,
        imagePath: CHART_OPTION_TYPE_TO_SVG_PATH[chartOptionType],
      };
    case ChartOptionType.TABLE:
    case ChartOptionType.GROUPED_BAR:
    case ChartOptionType.LINE:
    case ChartOptionType.PIE:
    case ChartOptionType.SCATTER:
    case ChartOptionType.HEATMAP:
      return {
        ...baseChartOption,
        createVisualizationConfig: (dataFrameColumns: DataFrameColumns) =>
          getDefaultVisualizationConfigGQL(
            dataFrameColumns,
            CHART_OPTION_TYPE_TO_VISUALIZATION_TYPE[chartOptionType]
          ),
      };
    case ChartOptionType.STACKED_BAR:
      return {
        ...baseChartOption,
        createVisualizationConfig: (dataFrameColumns: DataFrameColumns) => {
          const baseConfig = getDefaultVisualizationConfigGQL(
            dataFrameColumns,
            CHART_OPTION_TYPE_TO_VISUALIZATION_TYPE[chartOptionType]
          );
          return updateVisualization(baseConfig, {
            parameters: {
              ...baseConfig.parameters,
              barChartType: AnalyticsVisualizationBarChartType.STACKED,
            } as AnalyticsVisualizationParameters,
          });
        },
      };
    case ChartOptionType.BOXPLOT:
      return {
        ...baseChartOption,
        optionType: chartOptionType,
        disabledMessage: null,
        isBeta: true,
      };
    case ChartOptionType.EXPERIMENT_OPTIMIZATION:
      return {
        ...baseChartOption,
        optionType: chartOptionType,
        disabledMessage: null,
        showLabel: true,
        isBeta: true,
      };
    default:
      assert(false, 'Encountered an unsupported visualization subtype');
  }
}

/**
 * Chart view creation options. The options are grouped under their ChartTypeCategory.
 */
function getChartOptions(): Array<{
  chartTypeCategory: ChartTypeCategory;
  chartOptions: Array<ChartOption>;
}> {
  const options = [
    {
      chartTypeCategory: ChartTypeCategory.TABLE,
      chartOptions: [ChartOptionType.TABLE].map((subtype) => makeChartTypeOption(subtype)),
    },
    {
      chartTypeCategory: ChartTypeCategory.BAR,
      chartOptions: [ChartOptionType.GROUPED_BAR, ChartOptionType.STACKED_BAR].map((subtype) =>
        makeChartTypeOption(subtype)
      ),
    },
    {
      chartTypeCategory: ChartTypeCategory.OTHER,
      chartOptions: [
        ChartOptionType.LINE,
        ChartOptionType.PIE,
        ChartOptionType.SCATTER,
        ChartOptionType.HEATMAP,
        ChartOptionType.REGRESSION,
        ...(isAnalysisToolBoxPlotEnabled() ? [ChartOptionType.BOXPLOT] : []),
      ].map((subtype) => makeChartTypeOption(subtype)),
    },
  ];
  if (isExperimentOptimizationEnabled()) {
    options.push({
      chartTypeCategory: ChartTypeCategory.ML,
      chartOptions: [ChartOptionType.EXPERIMENT_OPTIMIZATION].map((subtype) => makeChartTypeOption(subtype)),
    });
  }
  return options;
}

/** @package */
export interface ChartButtonProps {
  chartType: ChartOptionType;
  isDisabled: boolean;
  imagePath: string;
  onClick: () => void;
  label: string;
  isBeta?: true;
}

export function ChartButton({
  chartType,
  isDisabled,
  imagePath,
  onClick,
  label,
  isBeta,
}: ChartButtonProps): JSX.Element {
  return (
    <Tooltip title={label}>
      <div
        className={css(chartButtonStyles.chartOption, isDisabled && chartButtonStyles.chartOptionDisabled)}
        onClick={isDisabled ? undefined : onClick}
        {...getTestProps({component: 'ChartButton', element: 'root', key: chartType})}
      >
        {isBeta && (
          <div className={css(chartButtonStyles.betaTag)}>
            <Tag color={TagColor.BETA} size={TagSize.SMALL} text="BETA" />
          </div>
        )}
        <img className={css(chartButtonStyles.chartImg)} src={staticURL(imagePath)} />
      </div>
    </Tooltip>
  );
}

export function ChartButtonWithLabel({
  chartType,
  isDisabled,
  imagePath,
  onClick,
  label,
  isBeta,
}: ChartButtonProps): JSX.Element {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className={css(
        chartButtonStyles.chartOptionWithLabel,
        isDisabled && chartButtonStyles.chartOptionDisabled
      )}
      onClick={isDisabled ? undefined : onClick}
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
      {...getTestProps({component: 'ChartButton', element: 'root', key: chartType})}
    >
      <img
        className={css(chartButtonStyles.chartImgWithLabel, isHovered && chartButtonStyles.chartImgHovered)}
        src={staticURL(imagePath)}
      />
      <div className={css(chartButtonStyles.labelContainer)}>
        {isBeta && <Tag color={TagColor.BETA} size={TagSize.SMALL} text="BETA" />}
        <div className={css(chartButtonStyles.label, isHovered && chartButtonStyles.labelHovered)}>
          {label}
        </div>
      </div>
    </div>
  );
}

const BASE_BACKGROUND_AND_BORDER = {
  backgroundColor: Color.GRAY_50,
  border: '1px solid',
  borderColor: Color.GRAY_200,
  borderRadius: 4,
};
/** @package */
export const chartButtonStyles = StyleSheet.create({
  betaTag: {
    zIndex: 1,
    position: 'absolute',
    // The tag component comes with a 8px margin
    right: -7,
    top: 1,
  },
  chartOption: {
    width: 72,
    height: 48,
    display: 'inline-flex',
    marginRight: 4,
    marginBottom: 8,
    position: 'relative',
    cursor: 'pointer',
    ...BASE_BACKGROUND_AND_BORDER,
    ':hover': {
      backgroundColor: Color.GRAY_200,
      borderColor: Color.GRAY_200,
    },
  },
  chartImg: {
    width: 46,
    height: 25,
    margin: 'auto',
  },
  chartOptionDisabled: {
    cursor: 'not-allowed',
    backgroundColor: Color.GRAY_200,
  },
  chartOptionWithLabel: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    cursor: 'pointer',
  },
  chartImgWithLabel: {
    width: 45,
    height: 32,
    padding: 4,
    ...BASE_BACKGROUND_AND_BORDER,
  },
  chartImgHovered: {
    backgroundColor: Color.GRAY_200,
    borderColor: Color.GRAY_200,
  },
  label: {
    ...FONT_PROXIMA_13,
  },
  labelHovered: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  labelContainer: {
    marginLeft: 8,
  },
});

export interface AddChartViewPopoverProps {
  analysis: PipelineGraphQL;
  stepId: string;
  dataFrameId: string;
  onOpenStepInitializationView: (newSelectedItem: AnalysisMainViewItem) => void;
  onChangeSelectedRightSidebarItem: (newSelectedRightSidebarItem: AnalysisRightSidebarItem | null) => void;
  children: (openMenu: () => void) => JSX.Element;
}

export function AddChartViewMenu({
  analysis,
  stepId,
  dataFrameId,
  onOpenStepInitializationView,
  onChangeSelectedRightSidebarItem,
  onRequestClose,
}: Omit<AddChartViewPopoverProps, 'children'> & {onRequestClose: () => void}): JSX.Element {
  const step = getLegacyStepFromAnalysis(analysis, stepId);

  const datasetOutputStepData = getOutputStepDataForDataFrame(step, dataFrameId);
  assert(
    datasetOutputStepData != null,
    "Data frame's step has no output step data that contains the data frame"
  );
  const inputDataReference: DataReference = {stepId: step.id, stepDataName: datasetOutputStepData.name};

  return (
    <div
      className={css(addChartViewMenuStyles.selectViewPopover)}
      {...getTestProps({component: 'AddChartViewMenu', element: 'root'})}
    >
      <BenchlingQuery<GetDataFrameColumns, GetDataFrameColumnsVariables>
        query={GET_DATA_FRAME_COLUMNS_QUERY}
        variables={{dataFrameId}}
        renderLoading={() => <CenteredHorizontalThrobber />}
      >
        {({dataFrame}) => {
          assert(!isInaccessible(dataFrame), 'User does not have access to input data frame');
          return getChartOptions().map((chartTypeOption) => (
            <div
              className={css(addChartViewMenuStyles.chartTypeSection)}
              key={chartTypeOption.chartTypeCategory}
            >
              <FieldLabel label={chartTypeOption.chartTypeCategory.toUpperCase()} />
              <div className={css(addChartViewMenuStyles.chartOptionsSection)}>
                {chartTypeOption.chartOptions.map((chartOption) => {
                  const props = {
                    chartType: chartOption.optionType,
                    isDisabled: chartOption.disabledMessage != null,
                    key: chartOption.optionType,
                    label: chartOption.disabledMessage ?? String(chartOption.optionType),
                    isBeta: chartOption.isBeta,
                    imagePath: chartOption.imagePath,
                    onClick: () => {
                      const initialVisualizationConfig = isChartOptionWithVisualizationConfig(chartOption)
                        ? chartOption.createVisualizationConfig(dataFrame.allColumnsJson)
                        : undefined;

                      CHART_OPTION_TYPE_TO_CREATE_VIEW_FN[chartOption.optionType]({
                        analysis,
                        inputDataFrameId: dataFrame.id,
                        inputDataReference,
                        onOpenStepInitializationView,
                        onChangeSelectedRightSidebarItem,
                        initialVisualizationConfig,
                        chartOptionType: chartOption.optionType,
                        dataFrameColumns: dataFrame.allColumnsJson,
                      });

                      onRequestClose();
                    },
                  };

                  return chartOption.showLabel ? (
                    <ChartButtonWithLabel {...props} />
                  ) : (
                    <ChartButton {...props} />
                  );
                })}
              </div>
            </div>
          ));
        }}
      </BenchlingQuery>
    </div>
  );
}

const addChartViewMenuStyles = StyleSheet.create({
  chartOptionsSection: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  chartTypeSection: {
    marginBottom: 8,
  },
  selectViewPopover: {
    paddingTop: 16,
    paddingLeft: 16,
    // Actual paddingRight ends up being 16 because of the clickableChartOption marginRight (4)
    paddingRight: 12,
    // paddingBottom is not defined, but ends up being 16 because of clickableChartOption marginBottom (8) and
    // chartTypeSection marginBottom (8)
    width: 256,
  },
});

/**
 * Popover to select and add a chart view of a dataset in an analysis.
 */
export default function AddChartViewPopover({
  analysis,
  stepId,
  onOpenStepInitializationView,
  dataFrameId,
  onChangeSelectedRightSidebarItem,
  children,
}: AddChartViewPopoverProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Float
      isOpen={isOpen}
      onRequestClose={() => {
        setIsOpen(false);
      }}
      interaction={FloatingInteraction.CONTROLLED}
      placement={FloatingPlacement.BOTTOM_END}
      floatingElementOptions={{
        floatingBodyZIndex: ZINDEX_FLOATING_UI,
        floatingBodyClassName: css(addChartViewPopoverStyles.popdownBody),
      }}
      renderContent={() => (
        <AddChartViewMenu
          analysis={analysis}
          stepId={stepId}
          onOpenStepInitializationView={onOpenStepInitializationView}
          dataFrameId={dataFrameId}
          onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
          onRequestClose={() => {
            setIsOpen(false);
          }}
        />
      )}
    >
      {children(() => {
        setIsOpen(true);
      })}
    </Float>
  );
}

const addChartViewPopoverStyles = StyleSheet.create({
  popdownBody: {
    background: Colors.WHITE,
    borderRadius: 4,
    boxShadow: `0px 2px 6px ${Colors.GRAY_DARKER}`,
  },
});
